


















































































import { Component, Mixins, Prop } from "vue-property-decorator";
import { mapState } from "vuex";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import BrandsStore from "@/mixins/brandsStore";

// Enums
import { ProductTypes } from "@/data/enums/products";
import { DataModules } from "@/store/modules/data";

// Types
import { IProduct, IProductCategory } from "@/models/products";
import { IState } from "@/store";

@Component({
  computed: {
    ...mapState({
      rootCategories(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: this.categoriesModule,
          scope: this.rootCategoriesScope
        });
      },
      rootCategoriesScope(state: IState, getters) {
        return getters[`data/${this.categoriesModule}/rootCategoriesScope`](
          this.brandId,
          this.productTypes
        );
      }
    })
  }
})
export default class MultilevelCategoriesSelections extends Mixins(
  RequestPromises,
  BrandsStore
) {
  // Getters
  public rootCategoriesScope!: string;
  public rootCategories!: IProductCategory[];

  // Props
  @Prop({ default: "" })
  public productId!: IProduct["id"];

  @Prop({
    default: () => [ProductTypes.SINGLE_PRODUCT, ProductTypes.PRODUCT_BUNDLE]
  })
  public productTypes!: ProductTypes[];

  @Prop({ default: () => [] })
  public selectedProducts!: any[];

  @Prop({ default: () => [] })
  public selectedCategories!: any[];

  @Prop({ default: false })
  public isProcessing!: boolean;

  @Prop({ default: false })
  public isDisabled!: boolean;

  @Prop({ default: false })
  public disableBundles!: boolean;

  @Prop({ default: () => null })
  public detach!: Function;

  @Prop({ default: () => null })
  public reorder!: Function;

  @Prop({ default: () => [] })
  public disabledProducts!: IProduct["id"][];

  // Variables
  public categoriesModule: string = DataModules.CATALOGUE_CATEGORIES;

  public isLoading = true;
  public isReloading = false;
  public requestError = false;

  public created() {
    if (this.contentAvailable) {
      this.isLoading = true;
      this.loadData();
    }
  }

  public async loadData() {
    try {
      await this.getRootCategories();
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    }
    this.isLoading = false;
    this.isReloading = false;
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public getRootCategories(forceReload = false) {
    return this.$store.dispatch(`data/${this.categoriesModule}/list`, {
      productTypes: this.productTypes,
      scope: this.rootCategoriesScope,
      params: {
        order: "order",
        limit: 0,
        "filter[level]": 1,
        brand_id: this.brandId
      },
      vm: this,
      ignoreStored: forceReload
    });
  }

  public get categoriesWithProducts() {
    return this.selectedCategories.length
      ? this.selectedCategories
      : this.$_.map(this.selectedProducts, selectedProduct => {
          selectedProduct["parentCategories"] = this.getProductPath(
            this.rootCategories,
            selectedProduct.category_id
          );
          return selectedProduct;
        });
  }

  // Recursive function to go deep and get the full categories path of the product
  public getProductPath(categories, categoryId: IProductCategory["id"]) {
    let path = [];

    function dive(_data, _path, _, level) {
      for (let i = 0; i < _data.length; i++) {
        if (_data[i].id === categoryId) {
          path = _.concat(_path, [
            { id: _data[i].id, name: _data[i].name, order: level }
          ]);
        } else if (_.has(_data[i], "subcategories")) {
          dive(
            _data[i].subcategories,
            _.concat(_path, [
              { id: _data[i].id, name: _data[i].name, order: level }
            ]),
            _,
            level + 1
          );
        }
      }
    }

    dive(categories, [], this.$_, 0);

    return path;
  }

  public get isOption() {
    return this.productTypes.includes(ProductTypes.PRODUCT_OPTION);
  }

  public get isAttribute() {
    return this.productTypes.includes(ProductTypes.PRODUCT_ATTRIBUTE);
  }

  public get i18n() {
    return {
      emptyResultsTitle: this.isOption
        ? this.$t(`_.no_product_options_attached`)
        : this.isAttribute
        ? this.$t(`_.no_product_attributes_attached`)
        : this.$t(`_.no_products_selected`),
      emptyResultsMsg: this.isOption
        ? this.$t(`_sentence.no_product_options_attached_msg`)
        : this.isAttribute
        ? this.$t(`_sentence.no_product_attributes_attached_msg`)
        : this.$t(`_sentence.no_products_selected_msg`),
      label: this.isOption
        ? this.$t(`_.manage_selected_product_options`)
        : this.isAttribute
        ? this.$t(`_.manage_selected_product_attributes`)
        : this.$t(`_.manage_selected_products`)
    };
  }

  public get listKey() {
    return this.$_.map(this.categoriesWithProducts, product => product.id).join(
      "_"
    );
  }

  public async view(element) {
    this.$router.push({
      name: this.getProductRouteName,
      params: {
        productId: element.id
      }
    });
    return { cancelled: true };
  }

  public async openManageProductsModal() {
    if (this.isProcessing || this.isDisabled) {
      return;
    }

    this.$store.dispatch(
      `data/${DataModules.CATALOGUE}/openSelectProductsModal`,
      {
        props: {
          productTypes: this.productTypes,
          selectedProducts: this.selectedCategories.length
            ? this.$_.flatten(
                this.$_.map(this.selectedCategories, selectedCategory => {
                  return this.$_.map(selectedCategory.items, item => {
                    return this.$_.pick(item, ["id", "category_id", "name"]);
                  });
                })
              )
            : this.selectedProducts,
          disableBundles: this.disableBundles,
          disabledProducts: this.disabledProducts
        },
        events: {
          success: results => {
            this.$emit("selected", results);
          }
        }
      }
    );
  }

  public get getProductRouteName() {
    return this.isOption
      ? "adminCatalogueOption"
      : this.isAttribute
      ? "adminCatalogueAttribute"
      : "adminCatalogueProduct";
  }

  public get contentAvailable() {
    return this.$userCan("staff_list_products") && this.isOption
      ? this.$userCan("list_product_options_categories")
      : this.isAttribute
      ? this.$userCan("list_product_attributes_categories")
      : this.$userCan("list_product_categories");
  }

  public get getGuardFuncionalities() {
    return this.isOption
      ? ["staff_list_products", "list_product_options_categories"]
      : this.isAttribute
      ? ["staff_list_products", "list_product_attributes_categories"]
      : ["staff_list_products", "list_product_categories"];
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: this.categoriesModule,
      scope: this.rootCategoriesScope,
      vm: this
    });
  }
}
